import React, { useState } from 'react';
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  ClockIcon,
  PaperAirplaneIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { facebookPixel } from '../utils/facebookPixel';

// Custom CSS animations
const customStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes shimmer {
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  }
  
  @keyframes pulse-ring {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.1);
      opacity: 0;
    }
  }
  
  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
    }
    50% {
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.3), 0 0 30px rgba(0, 0, 0, 0.2);
    }
  }
  
  .animate-float {
    animation: float 3s ease-in-out infinite;
  }
  
  .animate-fade-in-up {
    animation: fade-in-up 1s ease-out;
  }
  
  .shimmer-effect {
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    background-size: 200px 100%;
    animation: shimmer 2s infinite;
  }
  
  /* Enhanced Input Styles */
  .input-enhanced {
    position: relative;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(145deg, #ffffff, #f8fafc);
    border: 2px solid #e2e8f0;
  }
  
  .input-enhanced:hover {
    border-color: #cbd5e1;
    background: linear-gradient(145deg, #f8fafc, #ffffff);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
  }
  
  .input-enhanced:focus {
    outline: none;
    border-color: #374151;
    background: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1);
    animation: glow 2s ease-in-out infinite;
  }
  
  .input-enhanced:focus::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #374151, #6b7280, #9ca3af, #d1d5db);
    background-size: 300% 300%;
    border-radius: inherit;
    z-index: -1;
    animation: pulse-ring 1.5s ease-out infinite;
  }
  
  .input-group {
    position: relative;
  }
  
  .input-group:hover .input-label {
    color: #374151;
    transform: translateY(-1px);
  }
  
  .input-group:focus-within .input-label {
    color: #374151;
    font-weight: 600;
    transform: translateY(-2px);
  }
  
  .input-label {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .input-indicator {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d1d5db;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0;
  }
  
  .input-group:hover .input-indicator {
    opacity: 1;
    background: #9ca3af;
    transform: translateY(-50%) scale(1.2);
  }
  
  .input-group:focus-within .input-indicator {
    opacity: 1;
    background: #374151;
    transform: translateY(-50%) scale(1.4);
    box-shadow: 0 0 10px rgba(55, 65, 81, 0.5);
  }
  
  /* Textarea specific styles */
  .textarea-enhanced {
    resize: none;
    min-height: 120px;
  }
  
  .textarea-enhanced:hover {
    min-height: 130px;
  }
  
  .textarea-enhanced:focus {
    min-height: 140px;
  }
  
  /* Select specific styles */
  .select-enhanced {
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
    background-position: right 16px center !important;
    background-repeat: no-repeat !important;
    background-size: 16px 16px !important;
    padding-right: 48px !important;
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
  }
  
  .select-enhanced:hover {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23374151' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
    background-position: right 16px center !important;
    background-repeat: no-repeat !important;
    background-size: 16px 16px !important;
  }
  
  .select-enhanced:focus {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
    background-position: right 16px center !important;
    background-repeat: no-repeat !important;
    background-size: 16px 16px !important;
  }
  
  /* Remove default select styling completely */
  .select-enhanced::-ms-expand {
    display: none;
  }
  
  /* Fix dropdown positioning */
  .select-enhanced option {
    background: #ffffff;
    color: #374151;
    padding: 12px 16px;
    font-weight: 500;
  }
  
  .select-enhanced option:hover {
    background: #f3f4f6;
  }
  
  .select-enhanced option:checked {
    background: #e5e7eb;
    font-weight: 600;
  }
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://139.59.98.59/api'}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        // Track Facebook Pixel Lead event
        facebookPixel.trackLead({
          content_name: 'Contact Form Submission',
          value: 0,
          currency: 'MMK'
        });
        
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        console.error('Failed to send message:', data.message);
        // You could add error handling UI here
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // You could add error handling UI here
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: 'Address',
      details: ['No.27, Kan Taw Street,', 'Laydaukkan Ward, Thingangyun, Yangon'],
      color: 'text-gray-700'
    },
    {
      icon: PhoneIcon,
      title: 'Phone',
      details: ['+959 943000730'],
      color: 'text-gray-700'
    },
    {
      icon: EnvelopeIcon,
      title: 'Email',
      details: ['support@signaturestylesmm.com'],
      color: 'text-gray-700'
    },
    {
      icon: ClockIcon,
      title: 'Business Hours',
      details: ['Mon - Fri: 9:00 AM - 6:00 PM', 'Sat: 9:00 AM - 4:00 PM', 'Sun: Closed'],
      color: 'text-gray-700'
    }
  ];

  const subjects = [
    'General Inquiry',
    'Product Question',
    'Order Support',
    'Technical Support',
    'Partnership',
    'Complaint',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Inject custom styles */}
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 animate-fade-in-up">
              We're Here to Help!
            </h1>
            <p className="text-xl lg:text-2xl font-medium mb-4 text-gray-200 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              "Have a question, need assistance, or want to know more about our products?"
            </p>
            <p className="text-lg lg:text-xl text-gray-300 max-w-4xl mx-auto animate-fade-in-up" style={{animationDelay: '0.4s'}}>
              Get in touch with us today! Fill out the form below and we'll get back to you as soon as possible.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Contact Info Cards - Top Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {contactInfo.map((info, index) => (
            <div 
              key={index} 
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 cursor-pointer group relative overflow-hidden"
              style={{animationDelay: `${index * 0.2}s`}}
            >
              {/* Animated background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Floating decorative elements */}
              <div className="absolute top-2 right-2 w-6 h-6 bg-gray-200 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
              
              <div className="relative text-center">
                <div className="p-3 rounded-xl bg-gray-100 group-hover:bg-gray-200 transition-all duration-300 transform group-hover:rotate-6 group-hover:scale-110 mx-auto w-fit mb-3">
                  <info.icon className={`h-6 w-6 ${info.color} group-hover:animate-pulse`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-black transition-colors">
                  {info.title}
                </h3>
                <div className="space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 text-sm group-hover:text-gray-700 transition-colors leading-relaxed">
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
              
              {/* Hover indicator */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-gray-400 to-gray-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form - Takes 2 columns */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-6 transform hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <div className="mb-6 relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gray-100 rounded-full opacity-50"></div>
              <div className="relative">
                <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center">
                  <PaperAirplaneIcon className="h-6 w-6 text-gray-700 mr-3 animate-pulse" />
                  Send us a Message
                </h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </div>
            </div>

            {isSubmitted && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800">Thank you! Your message has been sent successfully.</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="input-group">
                  <label htmlFor="name" className="input-label block text-sm font-medium text-gray-700 mb-3">
                    Full Name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="input-enhanced w-full px-4 py-4 rounded-xl font-medium text-gray-900 placeholder-gray-500"
                      placeholder="Enter your full name"
                    />
                    <div className="input-indicator"></div>
                  </div>
                </div>
                <div className="input-group">
                  <label htmlFor="email" className="input-label block text-sm font-medium text-gray-700 mb-3">
                    Email Address *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="input-enhanced w-full px-4 py-4 rounded-xl font-medium text-gray-900 placeholder-gray-500"
                      placeholder="your.email@example.com"
                    />
                    <div className="input-indicator"></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="input-group">
                  <label htmlFor="phone" className="input-label block text-sm font-medium text-gray-700 mb-3">
                    Phone Number
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="input-enhanced w-full px-4 py-4 rounded-xl font-medium text-gray-900 placeholder-gray-500"
                      placeholder="+959 XXX XXX XXX"
                    />
                    <div className="input-indicator"></div>
                  </div>
                </div>
                <div className="input-group">
                  <label htmlFor="subject" className="input-label block text-sm font-medium text-gray-700 mb-3">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="input-enhanced select-enhanced w-full px-4 py-4 rounded-xl font-medium text-gray-900"
                  >
                    <option value="">Select a subject</option>
                    {subjects.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="message" className="input-label block text-sm font-medium text-gray-700 mb-3">
                  Message *
                </label>
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="input-enhanced textarea-enhanced w-full px-4 py-4 rounded-xl font-medium text-gray-900 placeholder-gray-500"
                    placeholder="Tell us how we can help you..."
                  />
                  <div className="input-indicator"></div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Map & Quick Actions */}
          <div className="space-y-6">

            {/* Map Section */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 group">
              <div className="p-4 border-b border-gray-200 relative">
                <div className="absolute top-0 right-0 w-16 h-16 bg-gray-100 rounded-full opacity-20 transform translate-x-8 -translate-y-8"></div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center relative">
                  <div className="p-2 bg-gray-100 rounded-lg mr-3 group-hover:bg-gray-200 transition-colors">
                    <MapPinIcon className="h-5 w-5 text-gray-700 group-hover:animate-bounce" />
                  </div>
                  Find Us on Map
                </h3>
                <p className="text-gray-600 mt-2 leading-relaxed">
                  Visit our store at Thingangyun, Yangon for personalized assistance.
                </p>
              </div>
              
              {/* Google Maps Embed */}
              <div className="relative h-64 group-hover:h-72 transition-all duration-500">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3819.8267842159887!2d96.13687731484!3d16.83894098842!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30c1eb1c6b0c1b1b%3A0x1b1b1b1b1b1b1b1b!2sThingangyun%20Township%2C%20Yangon%2C%20Myanmar%20(Burma)!5e0!3m2!1sen!2sus!4v1625097600000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Signature Styles Location"
                  className="w-full h-full transition-all duration-500 group-hover:brightness-110"
                />
                
                {/* Enhanced Map Overlay */}
                <div className="absolute bottom-4 left-4 bg-white bg-opacity-95 backdrop-blur-md rounded-xl p-4 shadow-xl transform group-hover:scale-110 transition-all duration-300">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-gray-700 rounded-full animate-pulse mr-3"></div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">Signature Styles</p>
                      <p className="text-xs text-gray-600">Thingangyun, Yangon</p>
                    </div>
                  </div>
                </div>
                
                {/* Interactive hover effect */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300 pointer-events-none"></div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-800 rounded-xl p-4 text-white">
              <h3 className="text-lg font-semibold mb-3">Need Immediate Help?</h3>
              <div className="space-y-2">
                <a 
                  href="tel:+959943000730" 
                  className="flex items-center p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  <PhoneIcon className="h-4 w-4 mr-2" />
                  <span>Call us now: +959 943000730</span>
                </a>
                <a 
                  href="mailto:support@signaturestylesmm.com" 
                  className="flex items-center p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                >
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  <span>Email: support@signaturestylesmm.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default Contact;
